import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CAvatar,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CFormSelect,
  CBadge,
  CSpinner,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import {
  cilPencil,
  cilReload,
  cilX,
  cilTrash,
  cilArrowRight,
  cilPaperclip,
} from "@coreui/icons";

import { apiClient } from "services/Api";
import DataTableComponent from "components/DataTableComponent";
import ToastNotification from "components/globals/ToastNotification";
import userAvatar from "assets/images/avatars/user.png";

const KickoffSessionList = () => {
  const navigate = useNavigate();
  const [toast, setToast] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [statusFilter, setStatusFilter] = useState("active");
  const [isLoading, setIsLoading] = useState(true);
  const [previewSession, setPreviewSession] = useState(null);

  useEffect(() => {
    const fetchSessions = async () => {
      setIsLoading(true);
      try {
        const archivedParam =
          statusFilter === "all"
            ? ""
            : `&archived=${statusFilter === "archived" ? 1 : 0}`;
        const response = await apiClient.get(
          `/kickoff-sessions/?year=${selectedYear}${archivedParam}`,
        );
        setSessions(response.data.sessions || []);
        setAvailableYears(response.data.available_years || []);
        if (
          response.data.available_years &&
          response.data.available_years.length > 0 &&
          !response.data.available_years.includes(Number(selectedYear))
        ) {
          setSelectedYear(response.data.available_years[0]);
        }
      } catch (error) {
        setToast({
          title: "Sessões de Arranque",
          message: "Erro a carregar as sessões de arranque",
          type: "error",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchSessions();
  }, [selectedYear, statusFilter]);

  const handleArchiveToggle = async (session) => {
    try {
      const archived = !session.archived;
      const response = await apiClient.patch(
        `/kickoff-sessions/${session.id}/`,
        { archived },
      );
      const updated = response.data;
      setSessions((prev) =>
        prev.map((item) => (item.id === updated.id ? updated : item)),
      );
      setPreviewSession(updated);
    } catch (error) {
      setToast({
        title: "Sessões de Arranque",
        message: "Erro ao arquivar a sessão de arranque",
        type: "error",
      });
    }
  };

  const handleDelete = async (sessionId) => {
    if (!window.confirm("Tem a certeza que quer eliminar este registo?")) {
      return;
    }
    try {
      await apiClient.delete(`/kickoff-sessions/${sessionId}/`);
      setSessions((prev) => prev.filter((item) => item.id !== sessionId));
      setPreviewSession(null);
    } catch (error) {
      setToast({
        title: "Sessões de Arranque",
        message: "Erro ao eliminar a sessão de arranque",
        type: "error",
      });
    }
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString("pt-PT");
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString("pt-PT");
  };

  const getClientOrCompany = (row) =>
    row.client_name || row.company_name || "-";

  const getApplicationLink = (row) => {
    if (!row.application) return "-";
    const slugMap = {
      innovation: "inovacao",
      idt: "idt",
      inter: "internacionalizacao",
    };
    const slug = slugMap[row.application_typology] || "inovacao";
    return `/candidaturas/${slug}/${row.application}`;
  };

  const columns = [
    {
      name: "Data",
      selector: (row) => formatDate(row.created_at),
      sortable: true,
      className: "text-center",
    },
    {
      name: "Cliente/Empresa",
      selector: (row) => getClientOrCompany(row),
      sortable: true,
    },
    {
      name: "Candidatura",
      selector: (row) =>
        row.application ? (
          <Link to={getApplicationLink(row)}>
            {row.application_designation || "Sem Designação"}
          </Link>
        ) : (
          "-"
        ),
      sortable: true,
    },
    {
      name: "Tipologia",
      selector: (row) => row.project_type || "-",
      sortable: true,
    },
    {
      name: "Criado por",
      selector: (row) => row.created_by_name || "-",
      sortable: true,
    },
  ];

  return (
    <>
      <ToastNotification toast={toast} setToast={setToast} />

      <CCard>
        <CCardHeader className="d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div>
            <h3 className="mb-0">Sessões de Arranque</h3>
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <CFormSelect
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ width: "180px" }}
            >
              <option value="active">Ativos</option>
              <option value="archived">Arquivados</option>
              <option value="all">Todos</option>
            </CFormSelect>
            <Link to="/sessoes-arranque/new">
              <CButton color="info" className="text-white">
                Nova Sessão
              </CButton>
            </Link>
          </div>
        </CCardHeader>
        <CCardBody>
          {isLoading ? (
            <div className="text-center">
              <CSpinner color="primary" />
            </div>
          ) : (
            <DataTableComponent
              columns={columns}
              data={sessions}
              years={availableYears.length > 0 ? availableYears : null}
              selectedYear={selectedYear}
              onYearChange={(newYear) => setSelectedYear(newYear)}
              onRowClicked={(row) => setPreviewSession(row)}
            />
          )}
        </CCardBody>
      </CCard>

      {previewSession && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "transparent",
            zIndex: 1040,
          }}
          onClick={() => setPreviewSession(null)}
          aria-hidden="true"
        />
      )}

      <COffcanvas
        placement="end"
        visible={Boolean(previewSession)}
        onHide={() => setPreviewSession(null)}
        backdrop={false}
        scrollable
        scroll
        style={{ width: "560px", maxWidth: "95vw" }}
      >
        <COffcanvasHeader className="border-bottom">
          <COffcanvasTitle className="d-flex align-items-center me-auto">
            <div>
              <div className="fw-semibold">Sessão de Arranque</div>
              <div className="text-medium-emphasis small">
                #{previewSession?.id || "-"}
              </div>
            </div>
          </COffcanvasTitle>
          <div className="d-flex align-items-center gap-2">
            {previewSession && (
              <>
                <CButton
                  color="info"
                  size="sm"
                  className="text-white"
                  onClick={() =>
                    navigate(`/sessoes-arranque/${previewSession.id}`)
                  }
                >
                  <CIcon icon={cilPencil} className="me-1" />
                  Editar
                </CButton>
                <CButton
                  color={previewSession.archived ? "secondary" : "warning"}
                  size="sm"
                  className="text-white"
                  onClick={() => handleArchiveToggle(previewSession)}
                >
                  <CIcon
                    icon={previewSession.archived ? cilReload : cilPaperclip}
                    className="me-1"
                  />
                  {previewSession.archived ? "Reativar" : "Arquivar"}
                </CButton>
                <CButton
                  color="danger"
                  size="sm"
                  className="text-white"
                  onClick={() => handleDelete(previewSession.id)}
                >
                  <CIcon icon={cilTrash} className="me-1" />
                  Remover
                </CButton>
              </>
            )}
            <CButton
              color="link"
              className="text-body p-0"
              onClick={() => setPreviewSession(null)}
              title="Fechar"
            >
              <CIcon icon={cilX} size="lg" />
            </CButton>
          </div>
        </COffcanvasHeader>
        <COffcanvasBody
          style={
            previewSession?.archived
              ? { backgroundColor: "var(--cui-tertiary-bg)" }
              : undefined
          }
        >
          {previewSession ? (
            <div className="d-flex flex-column gap-3">
              <div className="d-flex align-items-center gap-3">
                <CAvatar
                  src={previewSession.created_by_avatar_url || userAvatar}
                  size="lg"
                />
                <div className="flex-grow-1">
                  <div className="fw-semibold">
                    {previewSession.created_by_name || "-"}
                  </div>
                  <div className="text-medium-emphasis small">
                    Criado em {formatDateTime(previewSession.created_at)} •
                    Atualizado em {formatDateTime(previewSession.updated_at)}
                  </div>
                </div>
                {previewSession.archived && (
                  <CBadge color="secondary">Arquivado</CBadge>
                )}
              </div>

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  Cliente/Empresa
                </div>
                <div className="fw-semibold">
                  {getClientOrCompany(previewSession)}
                </div>
              </div>

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="text-uppercase text-medium-emphasis small">
                    Candidatura
                  </div>

                  {previewSession.application && (
                    <CButton
                      color="primary"
                      size="sm"
                      className="text-white"
                      onClick={() =>
                        navigate(getApplicationLink(previewSession))
                      }
                    >
                      Ir
                      <CIcon icon={cilArrowRight} className="ms-2" />
                    </CButton>
                  )}
                </div>
                <div className="fw-semibold">
                  {previewSession.application_designation || "-"}
                </div>
              </div>

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  Tipologia
                </div>
                <div className="fw-semibold">
                  {previewSession.project_type || "-"}
                </div>
              </div>

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  Contactos
                </div>
                <div className="fw-semibold">
                  {(previewSession.contacts || []).length}
                </div>
              </div>

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  Mercados pós-projeto
                </div>
                <div className="fw-semibold">
                  {(previewSession.post_project_markets || []).length}
                </div>
              </div>

              {previewSession.constraints && (
                <div className="border rounded-3 p-3 bg-body-tertiary">
                  <div className="text-uppercase text-medium-emphasis small">
                    Condicionantes
                  </div>
                  <div className="fw-semibold">
                    {previewSession.constraints}
                  </div>
                </div>
              )}

              {previewSession.company_activity_summary && (
                <div className="border rounded-3 p-3 bg-body-tertiary">
                  <div className="text-uppercase text-medium-emphasis small">
                    Resumo da atividade
                  </div>
                  <div
                    className="mt-2"
                    dangerouslySetInnerHTML={{
                      __html: previewSession.company_activity_summary,
                    }}
                  />
                </div>
              )}

              {previewSession.investment_list && (
                <div className="border rounded-3 p-3 bg-body-tertiary">
                  <div className="text-uppercase text-medium-emphasis small">
                    Lista Investimento
                  </div>
                  <div
                    className="mt-2"
                    dangerouslySetInnerHTML={{
                      __html: previewSession.investment_list,
                    }}
                  />
                </div>
              )}

              {previewSession.innovation_aspects && (
                <div className="border rounded-3 p-3 bg-body-tertiary">
                  <div className="text-uppercase text-medium-emphasis small">
                    Inovação/Aspetos diferenciadores
                  </div>
                  <div
                    className="mt-2"
                    dangerouslySetInnerHTML={{
                      __html: previewSession.innovation_aspects,
                    }}
                  />
                </div>
              )}

              {previewSession.product && (
                <div className="border rounded-3 p-3 bg-body-tertiary">
                  <div className="text-uppercase text-medium-emphasis small">
                    Produto
                  </div>
                  <div
                    className="mt-2"
                    dangerouslySetInnerHTML={{
                      __html: previewSession.product,
                    }}
                  />
                </div>
              )}

              {previewSession.process && (
                <div className="border rounded-3 p-3 bg-body-tertiary">
                  <div className="text-uppercase text-medium-emphasis small">
                    Processo
                  </div>
                  <div
                    className="mt-2"
                    dangerouslySetInnerHTML={{
                      __html: previewSession.process,
                    }}
                  />
                </div>
              )}

              {previewSession.organizational && (
                <div className="border rounded-3 p-3 bg-body-tertiary">
                  <div className="text-uppercase text-medium-emphasis small">
                    Organizacional
                  </div>
                  <div
                    className="mt-2"
                    dangerouslySetInnerHTML={{
                      __html: previewSession.organizational,
                    }}
                  />
                </div>
              )}

              {previewSession.marketing && (
                <div className="border rounded-3 p-3 bg-body-tertiary">
                  <div className="text-uppercase text-medium-emphasis small">
                    Marketing
                  </div>
                  <div
                    className="mt-2"
                    dangerouslySetInnerHTML={{
                      __html: previewSession.marketing,
                    }}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="text-muted">
              Clique numa sessão de arranque para ver o detalhe.
            </div>
          )}
        </COffcanvasBody>
      </COffcanvas>
    </>
  );
};

export default KickoffSessionList;
